import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Sparkles, Coins } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageUI";
import { PLAN_CATALOG, CREDIT_VALUE_NOTE, PLAN_ORDER, studioNote } from "@/lib/plans";
import { PAID_PLANS } from "@/lib/creative";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const startCheckout = async (pkg, setBusy) => {
  setBusy(pkg);
  try {
    const { data } = await api.post("/billing/checkout", { package_id: pkg, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(errText(e, "Could not start checkout. Please try again."));
    setBusy(null);
  }
};

const CreditPacks = ({ user, busy, setBusy }) => {
  const paid = PAID_PLANS.includes(user?.plan);
  const { data: packs } = useQuery({ queryKey: ["credit-packs", user?.plan], queryFn: () => api.get("/payments/credit-packs").then(r => r.data), enabled: paid });
  const mine = (packs || []).filter(p => p.plan === user?.plan);
  return (
    <section className="panel credit-packs" id="credit-packs" data-testid="credit-packs-section">
      <span className="eyebrow"><Coins size={11} /> CREDIT PACKS</span>
      <h2>Top up your Studio credits</h2>
      <p className="studio-muted">{CREDIT_VALUE_NOTE} Bigger plans get bonus credits on every pack.</p>
      {!paid ? <p data-testid="credit-packs-upgrade-note">Credit packs and the AI Creative Studio are available on paid plans. Choose a plan above to get started.</p> :
        <div className="pack-grid">
          {mine.map(p => (
            <article className="pack-card" key={p.id} data-testid={`credit-pack-${p.id}`}>
              <b>{p.credits.toLocaleString()} credits</b>
              {p.bonus_pct > 0 && <span className="pack-bonus">{p.base_credits.toLocaleString()} + {p.bonus_pct}% {user.plan} bonus</span>}
              <div className="pack-price" data-testid={`pack-price-${p.id}`}><b>{`$${p.amount}`}</b><span>one-time</span></div>
              <button className="primary-btn full" disabled={busy === p.id} onClick={() => startCheckout(p.id, setBusy)} data-testid={`buy-pack-${p.id}`}>{busy === p.id ? "Redirecting…" : "Buy pack"}</button>
            </article>
          ))}
        </div>}
    </section>
  );
};

export default function Plans() {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const { data: sub } = useQuery({ queryKey: ["billing-subscription"], queryFn: () => api.get("/billing/subscription").then(r => r.data) });
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [params] = useSearchParams();
  const { data: discounts } = useQuery({ queryKey: ["studio-discounts"], queryFn: () => api.get("/payments/studio-discounts").then(r => r.data) });
  const rank = PLAN_ORDER.indexOf(user?.plan || "Free");
  const periodEnd = sub?.subscription_period_end ? new Date(sub.subscription_period_end).toLocaleDateString() : null;
  const pastDue = sub?.has_subscription && sub.subscription_status === "past_due";
  const curPkg = sub?.has_subscription && !pastDue ? sub.subscription_package : null;
  const TIER = { Starter: 1, Growth: 2, Scale: 3 };

  const billingAction = async (key, path, body, okMsg) => {
    setBusy(key);
    try {
      await api.post(path, body);
      await qc.invalidateQueries({ queryKey: ["billing-subscription"] });
      if (refreshUser) await refreshUser();
      toast.success(okMsg);
    } catch (e) { toast.error(errText(e, "Something went wrong. Please try again.")); }
    finally { setBusy(null); }
  };
  const upgrade = (pkg, name) => {
    if (!window.confirm(`Switch to ${name} now? You'll be charged the prorated difference for the rest of this billing period.`)) return;
    billingAction(pkg, "/billing/change-plan", { package_id: pkg }, `You're now on ${name}`);
  };
  const openPortal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { package_id: "portal", origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errText(e, "Could not open billing settings.")); setBusy(null); }
  };

  const renderButton = (p) => {
    const slug = p.name.toLowerCase();
    const current = user?.plan === p.name;
    const pkg = `${slug}_${yearly ? "yearly" : "monthly"}`;
    if (p.name === "Free") return <button className="secondary-btn full" disabled data-testid="select-plan-free">{current ? "Current plan" : "Free forever"}</button>;
    if (p.enterprise) return <button className="secondary-btn full" onClick={() => nav("/contact")} data-testid="select-plan-enterprise">{current ? "Current plan" : "Contact sales"}</button>;
    if (curPkg) {
      const [curPlan, curInt] = [user?.plan, curPkg.endsWith("yearly") ? "yearly" : "monthly"];
      const thisInt = yearly ? "yearly" : "monthly";
      if (pkg === curPkg) return <button className="secondary-btn full" disabled data-testid={`select-plan-${slug}`}>Current plan</button>;
      const canUpgrade = (TIER[p.name] > TIER[curPlan] && !(curInt === "yearly" && thisInt === "monthly")) || (p.name === curPlan && curInt === "monthly" && thisInt === "yearly");
      if (canUpgrade) return <button className="primary-btn full" disabled={busy === pkg} onClick={() => upgrade(pkg, p.name)} data-testid={`select-plan-${slug}`}>{busy === pkg ? "Switching…" : p.name === curPlan ? "Switch to yearly" : `Upgrade to ${p.name}`}</button>;
      return <button className="secondary-btn full" disabled data-testid={`select-plan-${slug}`}>{TIER[p.name] < TIER[curPlan] ? "Included in your plan" : "Available at renewal"}</button>;
    }
    if (!current && PLAN_ORDER.indexOf(p.name) < rank) return <button className="secondary-btn full" disabled data-testid={`select-plan-${slug}`}>Included in your plan</button>;
    const label = current ? "Turn on auto-renew" : `Subscribe to ${p.name}`;
    return <button className={current ? "secondary-btn full" : "primary-btn full"} onClick={() => startCheckout(pkg, setBusy)} disabled={busy === pkg} data-testid={`select-plan-${slug}`}>{busy === pkg ? "Redirecting…" : label}</button>;
  };

  return (
    <>
      <PageHeader eyebrow="Plans" title="Choose the best plan for your brand." description={`Start free, then upgrade when your creator program is ready to scale. You're on the ${user?.plan || "Free"} plan.`} action={
        <div className="segmented" data-testid="plans-billing-toggle">
          <button className={!yearly ? "active" : ""} onClick={() => setYearly(false)} data-testid="plans-billing-monthly">Monthly</button>
          <button className={yearly ? "active" : ""} onClick={() => setYearly(true)} data-testid="plans-billing-yearly">Yearly −20%</button>
        </div>
      } />
      {pastDue && <div className="subscription-bar past-due" data-testid="subscription-past-due">
        <div>
          <b>Your last renewal payment failed, so your account is on the Free plan.</b>
          <small>Update your card, then retry the payment to get your plan back straight away.</small>
        </div>
        <div className="subscription-actions">
          <button className="secondary-btn" onClick={openPortal} disabled={busy === "portal"} data-testid="subscription-update-card">Update card</button>
          <button className="primary-btn" onClick={() => billingAction("retry", "/billing/retry", {}, "Payment successful, your plan is back")} disabled={busy === "retry"} data-testid="subscription-retry">{busy === "retry" ? "Retrying…" : "Retry payment"}</button>
        </div>
      </div>}
      {curPkg && <div className="subscription-bar" data-testid="subscription-bar">
        <div>
          <b data-testid="subscription-status-text">{sub.cancel_at_period_end ? `Auto-renew is off. Your ${user?.plan} plan ends on ${periodEnd}.` : `Your ${user?.plan} plan renews automatically on ${periodEnd}.`}</b>
          <small>Billed {curPkg.endsWith("yearly") ? "yearly" : "monthly"} · cancel anytime, you keep access until the end of the period.</small>
        </div>
        <div className="subscription-actions">
          <button className="secondary-btn" onClick={openPortal} disabled={busy === "portal"} data-testid="subscription-update-card">Update card</button>
          {sub.cancel_at_period_end
            ? <button className="primary-btn" onClick={() => billingAction("resume", "/billing/resume", {}, "Auto-renew is back on")} disabled={busy === "resume"} data-testid="subscription-resume">Turn auto-renew back on</button>
            : <button className="secondary-btn danger" onClick={() => window.confirm(`Cancel auto-renew? Your ${user?.plan} plan stays active until ${periodEnd}.`) && billingAction("cancel", "/billing/cancel", {}, "Auto-renew cancelled")} disabled={busy === "cancel"} data-testid="subscription-cancel">Cancel auto-renew</button>}
        </div>
      </div>}
      {params.get("studio") && <div className="studio-upgrade-banner" data-testid="studio-upgrade-banner"><Sparkles size={16} /> The AI Creative Studio is included with every paid plan, together with monthly Studio credits. Pick a plan below to start creating.</div>}
      <section className="plan-grid plan-grid-5" data-testid="plans-grid">
        {PLAN_CATALOG.map((p) => {
          const price = p.m === null ? "Custom" : `$${yearly ? p.y : p.m}`;
          return (
            <article className={`plan-card ${p.popular ? "popular" : ""} ${user?.plan === p.name ? "is-current" : ""}`} key={p.name} data-testid={`plan-${p.name.toLowerCase()}`}>
              {p.popular && <div className="popular-ribbon"><Sparkles size={14} />MOST POPULAR</div>}
              <span className="eyebrow">{p.name}</span>
              <div className="plan-price" data-testid={`plan-${p.name.toLowerCase()}-price`}><b>{price}</b>{p.m ? <span>/ month{yearly ? ` · $${p.y * 12} yearly` : ""}</span> : null}</div>
              <p>{p.desc}</p>
              <ul>{p.features.map((f) => <li key={f}><Check />{f}</li>)}</ul>
              {studioNote(p.name, discounts) && <small className="plan-studio-note" data-testid={`plan-${p.name.toLowerCase()}-studio`}>{studioNote(p.name, discounts)}{p.packBonus ? ` · +${p.packBonus}% bonus on credit packs` : ""}</small>}
              {renderButton(p)}
            </article>
          );
        })}
      </section>
      <CreditPacks user={user} busy={busy} setBusy={setBusy} />
      <p className="plans-test-note" data-testid="plans-test-note">Plans renew automatically every month or year until you cancel. Upgrades apply right away and you only pay the prorated difference. Payments run in Stripe <b>test mode</b>. Use card <b>4242 4242 4242 4242</b>, any future expiry, any CVC.</p>
    </>
  );
}
